/**
 * useMultiStrategyPublications — cross-plan subscription.
 *
 * Fans out one real-time listener per strategy via
 * ContentPublicationFirestoreService.subscribeForWindow and returns the
 * merged window-filtered list. Automatically re-subscribes when the set
 * of visible strategies or the window changes.
 *
 * Used by calendar Mode B (cross-plan window).
 */

import { useEffect, useMemo, useState } from 'react';
import { ContentPublicationFirestoreService } from '../services/ContentPublicationFirestoreService';
import type { ContentPublication } from '../types';

export interface UseMultiStrategyPublicationsReturn {
  publications: ContentPublication[];
  loading: boolean;
  error: Error | null;
}

export function useMultiStrategyPublications(
  projectId: string | null,
  strategyIds: string[],
  window: { start: Date; end: Date } | null,
): UseMultiStrategyPublicationsReturn {
  const [publications, setPublications] = useState<ContentPublication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const service = useMemo(() => new ContentPublicationFirestoreService(), []);

  // Stable key so we don't re-subscribe when the caller passes a fresh
  // array reference with the same ids.
  const idsKey = useMemo(() => [...strategyIds].sort().join('|'), [strategyIds]);
  const windowKey = useMemo(
    () => (window ? `${window.start.toISOString()}|${window.end.toISOString()}` : ''),
    [window],
  );

  useEffect(() => {
    if (!projectId || strategyIds.length === 0 || !window) {
      setPublications([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = service.subscribeForWindow(projectId, strategyIds, window, (pubs) => {
      setPublications(pubs);
      setLoading(false);
      setError(null);
    });
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, idsKey, windowKey, service]);

  return { publications, loading, error };
}
